import AppContext from "PFApp/app_context";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import { useSkillsBarQuery } from "PFCore/hooks/queries/insights/use_skills_bar_query";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useIsSkillFilterActive } from "../../../hooks/use_is_skill_filter_active";
import { useSupplyDemandSection } from "../../../hooks/use_supply_demand_section";
import BarPreview from "../../previews/bar_preview";
import { supplyTotalDemandChartConfig } from "./configs/supply_total_demand_chart_config";
import css from "./skills_delta.module.scss";
import SupplyTotalDemandSelectors from "./supply_total_demand_selectors";

export const SupplyTotalDemandSection = (): JSX.Element => {
  const {
    store: {
      pages: {
        insights: { selectedFilters }
      }
    }
  } = useContext(AppContext);

  const {
    skillCategories,
    setSkillCategories,
    selectedSkills,
    setSelectedSkills,
    showBy,
    setShowBy,
    bars,
    setBars,
    sortBy,
    setSortBy,
    topN,
    setTopN
  } = useSupplyDemandSection();

  const isSkillFilterActive = useIsSkillFilterActive();
  const { t } = useTranslation(["insights", "translation"]);

  const { data, isLoading, error } = useSkillsBarQuery({
    params: {
      ...selectedFilters,
      skill: isSkillFilterActive ? "" : selectedSkills.map((value) => value.value).join(","),
      skill_category: skillCategories.map((value) => value.value).join(","),
      sort_by: sortBy,
      show_by: showBy,
      top_n: topN,
      bars: bars.length > 0 ? bars.map((value) => value.value).join(",") : null
    }
  });

  if (isLoading && !data) {
    return <BarPreview animated content={t("translation:loading")} />;
  }

  const bar = data?.bar || [];
  const dropdown = data?.dropdown || [];

  const isNoData =
    !!error ||
    (bar && Array.isArray(bar) && bar.length === 0) ||
    (!isLoading && bar && !Array.isArray(bar) && bar.message === t("insights:noEmployeesFound"));

  // On a click a bar histogram, and it brings up a modal of all the roles comprising of the data
  // making up that bar, which the resource manager can then drill down into individual roles and see graphs

  return (
    <>
      <div className={css.skillsDeltaSection}>
        <h3>{t("insights:skillsSupplyTotalDemand")}</h3>
        <SupplyTotalDemandSelectors
          loading={isLoading}
          showBy={showBy}
          setShowBy={setShowBy}
          setSkillCategories={setSkillCategories}
          bars={bars}
          skills={data?.skills || []}
          selectedSkills={selectedSkills}
          setSelectedSkills={setSelectedSkills}
          setBars={setBars}
          sortBy={sortBy}
          setSortBy={setSortBy}
          topN={topN}
          setTopN={setTopN}
          skillCategories={skillCategories}
          dropdown={dropdown}
        />
      </div>
      {isNoData && <BarPreview content={t("insights:noData")} />}
      {isLoading && <BarPreview animated content={t("translation:loading")} />}
      {bar && Array.isArray(bar) && bar.length > 0 && !isLoading && (
        <EchartsWrapper
          height={650}
          minWidth={1000}
          options={supplyTotalDemandChartConfig(t("insights:saveImage"), bar, showBy, [
            t("insights:supplyAll"),
            t("insights:supplyRare"),
            t("insights:supplyDevelopmental"),
            t("insights:supplyCore"),
            t("insights:totalDemandRoles")
          ])}
        />
      )}
    </>
  );
};
